import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { AESEncryptedSecretStoragePayload } from "matrix-js-sdk/src/types";

import { ModuleRunner } from "../../modules/ModuleRunner";
import * as StorageAccess from "../../utils/StorageAccess";
import { IMatrixClientCreds } from "../../MatrixClientPeg";
import {
    persistApiAccessTokenInStorage, persistApiRefreshTokenInStorage, persistMatrixAccessTokenInStorage,
    persistMatrixRefreshTokenInStorage, tryDecryptToken
} from "../../utils/tokens/tokens";
import PlatformPeg from "../../PlatformPeg";
import {
    ACCESS_TOKEN_IV, API_ACCESS_TOKEN_STORAGE_KEY, API_AUTH_APP_ENABLED_KEY,
    API_AUTH_PROVIDER_KEY, API_BIOMETRIC_KEY, API_CONFIGURATION_KEY,
    API_CREATION_DATE_KEY, API_DEACTIVATED_KEY, API_DEVICES_KEY,
    API_EMAIL_KEY, API_EXPIRATION_TOKEN_KEY, API_FIRST_NAME_KEY,
    API_FISCAL_CODE_KEY, API_GOT_ID_KEY, API_LAST_NAME_KEY,
    API_LAST_UPDATE_KEY, API_MATRIX_NAME_KEY, API_PHONE_KEY,
    API_PIN_LOCK_KEY, API_PRIVATE_CONFIGURATION, API_REFRESH_TOKEN_STORAGE_KEY,
    API_ROLE_KEY, API_SHADOW_BANNED_KEY, API_STATUS_KEY,
    API_TELEMETRY_TOKEN_KEY, API_USER_ID_KEY, API_USERNAME_KEY,
    AUTH_PROVIDER_CREDENTIALS, AUTH_PROVIDER_TYPE, HAS_MX_REFRESH_TOKEN_STORAGE_KEY,
    HOMESERVER_URL_KEY, ID_SERVER_URL_KEY, MX_ACCESS_TOKEN_STORAGE_KEY,
    MX_DEVICE_ID_KEY, MX_FRESH_LOGIN_KEY, MX_HAS_PICKLE_KEY,
    MX_IS_GUEST_KEY, MX_REFRESH_TOKEN_STORAGE_KEY, MX_USER_ID_KEY,
    REFRESH_TOKEN_IV, TABLE_ACCOUNT_NAME_STORAGE, TABLE_PICKLEKEY_NAME_STORAGE,
} from "../constants";
import { IDthPrivateConfiguration, IDthUser } from "./models/httpClientModels";
import { decryptText, deepCloneObject, encryptText } from "../utils";
import { ExtendedMatrixClientCreds } from "@matrix-org/react-sdk-module-api/lib/lifecycles/CryptoSetupExtensions";

const logger = baseLogger.getChild("DthSessionStorage");

export class DthSessionStorage {

    /**
     * Memorizza le informazioni dell'utente corrente in una variabile statica per un accesso più rapido durante la sessione.
     */
    private static _currentUserInfo: IDthUser | undefined = undefined;

    /**
     * Memorizza le configurazioni private di DTH in una variabile statica per un accesso più rapido durante la sessione.
     */
    private static _privateConfiguration: IDthPrivateConfiguration | undefined = undefined;

    /**
     * Prova a prelevare l'username di accesso altrimenti ritorna null
     * @returns username o null se non disponibile
     */
    public static async tryGetUsername(): Promise<string | null> {
        return DthSessionStorage.getGenericItem("username") || DthSessionStorage.getCurrentUserInfo()?.email || null;
    }
    
    /**
     * Salva tutte le informazioni della sessione relative all'utente, inclusi credenziali, informazioni utente e contatti.
     * @param credentials Le credenziali del client Matrix da salvare.
     * @param user Le informazioni dell'utente da salvare.
     * @param authProvider Il provider di autenticazione utilizzato, se disponibile. Se non specificato, verrà mantenuto il valore esistente nella sessione o impostato su "credentials" se non presente.
     */
    public static async saveAllSessionInfo(
        credentials: IMatrixClientCreds,
        user: IDthUser,
        authProvider: AUTH_PROVIDER_TYPE | null): Promise<void> {

        await this.saveCredentials(credentials);
        await this.saveCurrentUserInfo(user, authProvider);
    }

    /**
     * Salva le informazioni dell'utente corrente nella sessione. 
     * Se authProvider è specificato, aggiorna anche il provider di autenticazione; 
     * altrimenti mantiene il valore esistente o lo imposta su "credentials" se non presente.
     * @param user Le informazioni dell'utente da salvare.
     * @param authProvider Il provider di autenticazione utilizzato, se disponibile.
     */
    public static saveCurrentUserInfo(user: IDthUser, authProvider: AUTH_PROVIDER_TYPE | null): void {
        if (authProvider != null || this.getGenericItem(API_AUTH_PROVIDER_KEY) == null) {
            this.setGenericItem(API_AUTH_PROVIDER_KEY, authProvider ?? AUTH_PROVIDER_CREDENTIALS);
        }
        if (user.authenticatorAppEnabled != null) this.setGenericItem(API_AUTH_APP_ENABLED_KEY, String(user.authenticatorAppEnabled));
        if (user.biometricAccessEnabled != null) this.setGenericItem(API_BIOMETRIC_KEY, String(user.biometricAccessEnabled));
        if (user.configuration != null) this.setGenericItem(API_CONFIGURATION_KEY, JSON.stringify(user.configuration));
        if (user.creationDate != null) this.setGenericItem(API_CREATION_DATE_KEY, user.creationDate);
        if (user.deactivated != null) this.setGenericItem(API_DEACTIVATED_KEY, String(user.deactivated));
        if (user.devices != null) this.setGenericItem(API_DEVICES_KEY, JSON.stringify(user.devices));
        if (user.email != null) this.setGenericItem(API_EMAIL_KEY, user.email);
        if (user.fiscalCode != null) this.setGenericItem(API_FISCAL_CODE_KEY, user.fiscalCode);
        if (user.firstName != null) this.setGenericItem(API_FIRST_NAME_KEY, user.firstName);
        if (user.gotId != null) this.setGenericItem(API_GOT_ID_KEY, user.gotId);
        if (user.id != null) this.setGenericItem(API_USER_ID_KEY, user.id);
        if (user.lastName != null) this.setGenericItem(API_LAST_NAME_KEY, user.lastName);
        if (user.lastUpdate != null) this.setGenericItem(API_LAST_UPDATE_KEY, user.lastUpdate);
        if (user.matrixName != null) this.setGenericItem(API_MATRIX_NAME_KEY, user.matrixName);
        if (user.phone != null) this.setGenericItem(API_PHONE_KEY, user.phone);
        if (user.pinLockEnabled != null) this.setGenericItem(API_PIN_LOCK_KEY, String(user.pinLockEnabled));
        if (user.role != null) this.setGenericItem(API_ROLE_KEY, user.role);
        if (user.shadowBanned != null) this.setGenericItem(API_SHADOW_BANNED_KEY, String(user.shadowBanned));
        if (user.status != null) this.setGenericItem(API_STATUS_KEY, user.status);
        if (user.telemetryToken != null) this.setGenericItem(API_TELEMETRY_TOKEN_KEY, user.telemetryToken);
        if (user.username != null) this.setGenericItem(API_USERNAME_KEY, user.username);

        // console.log("CURRENT USER INFO SAVED: " + JSON.stringify(user));

        // aggiorna la variabile statica con le nuove informazioni dell'utente
        // copia profonda per evitare modifiche accidentali
        try {
            DthSessionStorage._currentUserInfo = deepCloneObject(user);
        } catch (error) {
            logger.error("Errore nel salvataggio delle info dell'utente corrente nella variabile statica:", error);
        }

    }

    /**
     * Recupera le informazioni dell'utente corrente dalla sessione.
     * @returns Le informazioni dell'utente corrente, o null se non sono disponibili o in caso di errore nel recupero/parsing.
     */
    public static getCurrentUserInfo(): IDthUser | null {
        try {
            const id = this.getGenericItem(API_USER_ID_KEY);
            if (!id) return null;

            // se le informazioni dell'utente sono già state caricate in precedenza, 
            // restituiscile direttamente dalla variabile statica
            if (DthSessionStorage._currentUserInfo &&
                DthSessionStorage._currentUserInfo.id === id
            ) {
                try {
                    // restituisce sempre una copia profonda
                    return deepCloneObject(DthSessionStorage._currentUserInfo);
                } catch (error) {
                    logger.error("Errore nel recupero delle info dell'utente corrente dalla variabile statica:", error);
                }
            }


            const authProvider = this.getGenericItem(API_AUTH_PROVIDER_KEY) ?? AUTH_PROVIDER_CREDENTIALS;
            const authenticatorAppEnabled = this.getGenericItem(API_AUTH_APP_ENABLED_KEY) === "true";
            const biometricAccessEnabled = this.getGenericItem(API_BIOMETRIC_KEY) === "true";
            let configuration = undefined;
            try { configuration = JSON.parse(this.getGenericItem(API_CONFIGURATION_KEY) ?? "") } catch { }
            const creationDate = this.getGenericItem(API_CREATION_DATE_KEY) ?? "";
            const deactivated = this.getGenericItem(API_DEACTIVATED_KEY) === "true";
            let devices = [];
            try { devices = JSON.parse(this.getGenericItem(API_DEVICES_KEY) ?? "[]"); } catch { }
            const email = this.getGenericItem(API_EMAIL_KEY) ?? "";
            const firstName = this.getGenericItem(API_FIRST_NAME_KEY) ?? "";
            const fiscalCode = this.getGenericItem(API_FISCAL_CODE_KEY) ?? "";
            const gotId = this.getGenericItem(API_GOT_ID_KEY) ?? "";
            const lastName = this.getGenericItem(API_LAST_NAME_KEY) ?? "";
            const lastUpdate = this.getGenericItem(API_LAST_UPDATE_KEY) ?? "";
            const matrixName = this.getGenericItem(API_MATRIX_NAME_KEY) ?? "";
            const phone = this.getGenericItem(API_PHONE_KEY) ?? "";
            const pinLockEnabled = this.getGenericItem(API_PIN_LOCK_KEY) === "true";
            const role = this.getGenericItem(API_ROLE_KEY) ?? "";
            const shadowBanned = this.getGenericItem(API_SHADOW_BANNED_KEY) === "true";
            const status = this.getGenericItem(API_STATUS_KEY) ?? "";
            const telemetryToken = this.getGenericItem(API_TELEMETRY_TOKEN_KEY) ?? "";
            const username = this.getGenericItem(API_USERNAME_KEY) ?? "";

            const user: IDthUser = {
                id,
                authProvider,
                authenticatorAppEnabled,
                biometricAccessEnabled,
                configuration,
                creationDate,
                deactivated,
                devices,
                email,
                firstName,
                fiscalCode,
                gotId,
                lastName,
                lastUpdate,
                matrixName,
                phone,
                pinLockEnabled,
                role,
                shadowBanned,
                status,
                telemetryToken,
                username,
            };

            // aggiorna la variabile statica con le nuove informazioni dell'utente
            // copia profonda per evitare modifiche accidentali
            try {
                DthSessionStorage._currentUserInfo = deepCloneObject(user);
            } catch (error) {
                logger.error("Errore nel salvataggio delle info dell'utente corrente nella variabile statica:", error);
            }

            return user;
        } catch (error) {
            logger.error("Errore nel recupero delle info dell'utente corrente:", error);
            return null;
        }
    }

    /**
     * Salva le credenziali del client Matrix nella sessione, inclusi i token di accesso e refresh, e altre informazioni correlate.
     * @param credentials Le credenziali del client Matrix da salvare, che includono accessToken, refreshToken, apiAccessToken, apiRefreshToken, e altre informazioni necessarie per la sessione.
     */
    public static async saveCredentials(credentials: IMatrixClientCreds): Promise<void> {
        // console.log("MATRIX CREDENTIALS SAVED: ", credentials);
        // console.log("API ACCESS TOKEN SAVED: ", credentials.apiAccessToken);
        // console.log("DEVICE ID SAVED: ", credentials.deviceId);
        this.setGenericItem(HOMESERVER_URL_KEY, credentials.homeserverUrl);
        if (credentials.identityServerUrl) {
            this.setGenericItem(ID_SERVER_URL_KEY, credentials.identityServerUrl);
        }
        this.setGenericItem(API_USER_ID_KEY, credentials.apiUserId);
        this.setGenericItem(MX_USER_ID_KEY, credentials.userId);
        this.setGenericItem(MX_IS_GUEST_KEY, JSON.stringify(credentials.guest));

        //#region INFO DA SALVARE SU DB PER IL FALLBACK DEL SERVICE WORKER
        // Persist userId, deviceId and homeserver to IndexedDB as well, so the
        // service worker can read them directly without postMessage (which may
        // time out when the main thread is blocked by a synchronous React render).
        try {
            await StorageAccess.idbSave(TABLE_ACCOUNT_NAME_STORAGE, MX_USER_ID_KEY, credentials.userId);
            if (credentials.deviceId) {
                await StorageAccess.idbSave(TABLE_ACCOUNT_NAME_STORAGE, MX_DEVICE_ID_KEY, credentials.deviceId);
            }
            await StorageAccess.idbSave(TABLE_ACCOUNT_NAME_STORAGE, HOMESERVER_URL_KEY, credentials.homeserverUrl);
        } catch (e) {
            logger.warn("Failed to persist user info to IndexedDB for service worker", e);
        }
        //#endregion

        await persistApiAccessTokenInStorage(TABLE_ACCOUNT_NAME_STORAGE, credentials.apiAccessToken, credentials.pickleKey);

        this.setGenericItem(API_EXPIRATION_TOKEN_KEY, credentials.apiExpirationToken.toString());
        await persistApiRefreshTokenInStorage(TABLE_ACCOUNT_NAME_STORAGE, credentials.apiRefreshToken, credentials.pickleKey);

        await this.updateMatrixTokens(credentials.accessToken, credentials.refreshToken, credentials.pickleKey);

        if (credentials.pickleKey) {
            this.setGenericItem(MX_HAS_PICKLE_KEY, String(true));
        } else {
            if (this.getGenericItem(MX_HAS_PICKLE_KEY) === "true") {
                logger.error("Expected a pickle key, but none provided. Encryption may not work.");
            }
        }

        // if we didn't get a deviceId from the login, leave mx_device_id unset,
        // rather than setting it to "undefined".
        //
        // (in this case MatrixClient doesn't bother with the crypto stuff
        // - that's fine for us).
        if (credentials.deviceId) {
            this.setGenericItem(MX_DEVICE_ID_KEY, credentials.deviceId);
        }

        if (credentials.freshLogin) {
            this.setGenericItem(MX_FRESH_LOGIN_KEY, String(true));
        }

        ModuleRunner.instance.extensions.cryptoSetup?.persistCredentials({
            homeserverUrl: credentials.homeserverUrl,
            identityServerUrl: credentials.identityServerUrl,
            accessToken: credentials.accessToken,
            refreshToken: credentials.refreshToken,
            userId: credentials.userId,
            deviceId: credentials.deviceId,
            guest: credentials.guest,
            pickleKey: credentials.pickleKey,
            freshLogin: credentials.freshLogin,
            secureBackupKey: undefined, // non gestiamo backup crittografati per ora
        } as ExtendedMatrixClientCreds);

        console.log(`[DthSessionStorage] Session persisted for ${credentials.userId}`);
    }

    /**
     * Aggiorna i token Matrix (accessToken e refreshToken) nello storage.
     * @param credentials Le credenziali del client Matrix da cui prendere i token.
     */
    public static async updateMatrixTokens(
        accessToken: string,
        refreshToken: string | undefined,
        pickleKey: string | undefined)
        : Promise<void> {
        await persistMatrixAccessTokenInStorage(TABLE_ACCOUNT_NAME_STORAGE, accessToken, pickleKey);

        if (refreshToken) {
            await persistMatrixRefreshTokenInStorage(TABLE_ACCOUNT_NAME_STORAGE, refreshToken, pickleKey);
        }
    }

    /**
     * Recupera i token Matrix (accessToken e refreshToken) dallo storage.
     * @param pickleKey La chiave di cifratura per decriptare i token.
     * @returns Un oggetto contenente accessToken e refreshToken (se presenti).
     */
    public static async getMatrixTokens(pickleKey?: string): Promise<{ accessToken?: string, refreshToken?: string }> {
        const pickleKeyValue: string | undefined = pickleKey || (await this.getPickleKey());

        const accessToken = await this.getDecryptedStoredToken(pickleKeyValue, MX_ACCESS_TOKEN_STORAGE_KEY, ACCESS_TOKEN_IV);
        let refreshToken: string | undefined = undefined;
        if (this.getGenericItem(HAS_MX_REFRESH_TOKEN_STORAGE_KEY) === "true") {
            refreshToken = await this.getDecryptedStoredToken(pickleKeyValue, MX_REFRESH_TOKEN_STORAGE_KEY, REFRESH_TOKEN_IV);
        }
        return { accessToken, refreshToken };
    }

    /**
     * Recupera le informazioni del client Matrix memorizzate nella sessione. 
     * La sessione potrebbe non essere valida, poiché non viene testata per la coerenza qui.
     * @returns {Object} Le informazioni del client Matrix - vedere l'implementazione per le variabili.
     */
    public static async getCredentials(): Promise<IMatrixClientCreds> {
        const hsUrl = this.getGenericItem(HOMESERVER_URL_KEY) ?? "";
        const isUrl = this.getGenericItem(ID_SERVER_URL_KEY) ?? "";
        const apiUserId = this.getGenericItem(API_USER_ID_KEY) ?? "";
        const userId = this.getGenericItem(MX_USER_ID_KEY) ?? "";
        const deviceId = this.getGenericItem(MX_DEVICE_ID_KEY) ?? "";
        const freshLogin = this.getGenericItem(MX_FRESH_LOGIN_KEY) === "true";

        let accessToken: string = "";
        let refreshToken: string | undefined = undefined;
        let apiAccessToken: string = "";
        let apiRefreshToken: string | undefined = undefined;
        let apiExpirationToken: number = 0;
        let pickleKey: string | undefined = undefined;

        // solo se c'è l'user id, viene provato il prelievo dei token
        if (userId && deviceId) {
            pickleKey = await this.getPickleKey();

            if (!pickleKey)
                console.error(`[DthSessionStorage] No pickle key available for ${userId}|${deviceId}`);

            accessToken = await this.getDecryptedStoredToken(pickleKey, MX_ACCESS_TOKEN_STORAGE_KEY, ACCESS_TOKEN_IV) ?? "";

            if (this.getGenericItem(HAS_MX_REFRESH_TOKEN_STORAGE_KEY) === "true") {
                refreshToken = await this.getDecryptedStoredToken(pickleKey, MX_REFRESH_TOKEN_STORAGE_KEY, REFRESH_TOKEN_IV) ?? "";
            }
            apiAccessToken = await this.getDecryptedStoredToken(pickleKey, API_ACCESS_TOKEN_STORAGE_KEY, ACCESS_TOKEN_IV) ?? "";
            apiRefreshToken = await this.getDecryptedStoredToken(pickleKey, API_REFRESH_TOKEN_STORAGE_KEY, REFRESH_TOKEN_IV) ?? "";

            apiExpirationToken = Number(this.getGenericItem(API_EXPIRATION_TOKEN_KEY) ?? "0");

            // if we pre-date storing "mx_has_access_token", but we retrieved an access
            // token, then we should say we have an access token
            // const hasAccessToken = this.getGenericItem(HAS_MX_ACCESS_TOKEN_STORAGE_KEY) === "true" || !!accessToken;
            // const hasRefreshToken = this.getGenericItem(HAS_MX_REFRESH_TOKEN_STORAGE_KEY) === "true" || !!refreshToken;
            // const hasApiAccessToken = this.getGenericItem(HAS_API_ACCESS_TOKEN_STORAGE_KEY) === "true" || !!apiAccessToken;
            // const hasApiRefreshToken = this.getGenericItem(HAS_API_REFRESH_TOKEN_STORAGE_KEY) === "true" || !!apiRefreshToken;
        }

        let isGuest: boolean;
        if (this.getGenericItem(MX_IS_GUEST_KEY) !== null) {
            isGuest = this.getGenericItem(MX_IS_GUEST_KEY) === "true";
        } else {
            // legacy key name
            isGuest = this.getGenericItem("matrix-is-guest") === "true";
        }

        const credentials: IMatrixClientCreds = {
            homeserverUrl: hsUrl,
            identityServerUrl: isUrl,
            apiUserId: apiUserId,
            accessToken,
            refreshToken,
            apiAccessToken,
            apiExpirationToken,
            apiRefreshToken,
            userId,
            deviceId,
            guest: isGuest,
            pickleKey: pickleKey,
            freshLogin: freshLogin
        };

        // console.log("[DthSessionStorage] GET MATRIX CREDENTIALS SAVED: ", credentials);
        return credentials;
    }

    /**
     * Recupera un token memorizzato nella sessione, decriptandolo se necessario.
     * @param pickleKey La chiave di cifratura (pickle key) da utilizzare per decriptare il token, 
     * se è memorizzato in forma cifrata. Se non è disponibile, si tenterà comunque di recuperare il token in chiaro (se presente), 
     * ma la decriptazione fallirà se il token è cifrato.
     * @param storageKey La chiave specifica utilizzata per memorizzare il token (ad esempio, API_ACCESS_TOKEN_STORAGE_KEY). 
     * Questa chiave viene utilizzata per recuperare il token dalla sessione e, se è cifrato, per tentare di decriptarlo con la pickle key fornita.
     * @param iv Il vettore di inizializzazione (IV) utilizzato per la decriptazione del token, se è cifrato.
     * @returns Una Promise che risolve il token decriptato o undefined se non è disponibile.
     */
    public static async getDecryptedStoredToken(
        pickleKey: string | undefined,
        storageKey: string,
        iv: string): Promise<string | undefined> {
        const token: string | AESEncryptedSecretStoragePayload | undefined = await this.getStoredToken(TABLE_ACCOUNT_NAME_STORAGE, storageKey);
        return await tryDecryptToken(pickleKey, token!, iv);
    }

    /**
     * Salva la configurazione privata dell'utente
     * @param privateConfiguration La configurazione privata dell'utente da salvare.
     */
    public static savePrivateConfiguration(privateConfiguration: IDthPrivateConfiguration): void {
        this.setGenericItem(API_PRIVATE_CONFIGURATION, JSON.stringify(privateConfiguration));
        // console.log("[DthSessionStorage] PRIVATE CONFIGURATION SAVED: " + JSON.stringify(privateConfiguration));
        try {
            DthSessionStorage._privateConfiguration = deepCloneObject(privateConfiguration);
        } catch (error) {
            logger.error("Errore nel salvataggio della configurazione privata nella variabile statica:", error);
        }
    }

    /**
     * Recupera la configurazione privata dell'utente
     * @returns La configurazione privata dell'utente, o null se non è disponibile o in caso di errore nel recupero/parsing.
     */
    public static getPrivateConfiguration(): IDthPrivateConfiguration | null {
        try {
            if (DthSessionStorage._privateConfiguration) {
                try {
                    return deepCloneObject(DthSessionStorage._privateConfiguration);
                } catch (error) {
                    logger.error("Errore nel recupero della configurazione privata dalla variabile statica:", error);
                }
            }

            const privateConfigurationJSON = this.getGenericItem(API_PRIVATE_CONFIGURATION);
            if (!privateConfigurationJSON) return null;
            const parsed: IDthPrivateConfiguration = JSON.parse(privateConfigurationJSON);

            try {
                DthSessionStorage._privateConfiguration = deepCloneObject(parsed);
            } catch (error) {
                logger.error("Errore nel salvataggio della configurazione privata nella variabile statica:", error);
            }

            return parsed;
        } catch (error) {
            logger.error("Errore nel recupero delle info della configurazione corrente:", error);
            return null;
        }
    }

    /**
     * Recupera un token, come memorizzato da `persistCredentials`
     * Tenta di migrare il token da localStorage a IndexedDB
     * @param storageKey chiave utilizzata per memorizzare il token, ad esempio ACCESS_TOKEN_STORAGE_KEY
     * @returns Promise che risolve in un token o undefined
     */
    private static async getStoredToken(idTableName: string, storageKey: string): Promise<string | AESEncryptedSecretStoragePayload | undefined> {
        let token: string | AESEncryptedSecretStoragePayload | undefined;
        try {
            token = await StorageAccess.idbLoad(idTableName, storageKey);
        } catch (e) {
            console.error(`[DthSessionStorage] StorageManager.idbLoad failed for account:${storageKey}`, e);
        }
        if (!token) {
            token = this.getGenericItem(storageKey) ?? undefined;
            if (token) {
                try {
                    // try to migrate access token to IndexedDB if we can
                    await StorageAccess.idbSave(idTableName, storageKey, token);
                    window.localStorage.removeItem(storageKey);
                } catch (e) {
                    console.error(`[DthSessionStorage] migration of token ${storageKey} to IndexedDB failed`, e);
                }
            }
        }
        return token;
    }

    /**
     * Recupera la pickle key per l'utente e dispositivo correnti, se disponibile.
     * @returns Una Promise che risolve la pickle key o undefined se non è disponibile o in caso di errore nel recupero. 
     * La pickle key è necessaria per decriptare i token memorizzati in forma cifrata. 
     * Se non è disponibile, i token cifrati non potranno essere decriptati correttamente.
     */
    public static async getPickleKey(): Promise<string | undefined> {
        const userId = this.getGenericItem(MX_USER_ID_KEY);
        if (!userId) {
            logger.warn("User id is required to get Pickle key");
            return undefined;
        }
        const deviceId = this.getGenericItem(MX_DEVICE_ID_KEY);
        if (!deviceId) {
            logger.warn("Device id is required to get Pickle key");
            return undefined;
        }

        const hasPickleKey = this.getGenericItem(MX_HAS_PICKLE_KEY) === "true";
        const pickleKey = hasPickleKey ? await PlatformPeg.get()?.getPickleKey(userId, deviceId) : undefined;

        if (!pickleKey) {
            logger.error("Pickle key not found or decryption not available");
            return undefined;
        }

        console.log(`[DthSessionStorage] Got pickle key for ${userId}|${deviceId}`);
        return pickleKey;
    }

    public static setGenericItem(key: string, value: string): void {
        window.localStorage.setItem(key, value);
    }

    public static getGenericItem(key: string): string | null {
        return window.localStorage.getItem(key);
    }

    public static removeGenericItem(key: string): void {
        window.localStorage.removeItem(key);
    }

    public static async setEncryptedGenericItem(key: string, value: string, secret: string): Promise<void> {
        const encrypted = await encryptText(value, secret);
        window.localStorage.setItem(key, encrypted);
    }

    public static async getEncryptedGenericItem(key: string, secret: string): Promise<string | null> {
        const encrypted = window.localStorage.getItem(key);
        if (!encrypted) return null;

        return await decryptText(encrypted, secret);
    }

    /**
     * Cancella tutte le informazioni memorizzate nella sessione, inclusi token, informazioni utente e contatti.
     * @param deleteEverything Se true, cancella anche le impostazioni dell'utente; 
     * se false, mantiene le impostazioni dell'utente (come la lingua).
     */
    public static async clearStorage(deleteEverything: boolean = false): Promise<void> {
        // reset current user info in memory
        DthSessionStorage._currentUserInfo = undefined;
        // reset private configuration in memory
        DthSessionStorage._privateConfiguration = undefined;

        if (window.sessionStorage) {
            window.sessionStorage.clear();
        }

        if (window.localStorage) {
            // Gestione informazioni da non cancellare se deleteEverything è false

            // get the currently defined device language, if set, so we can restore it later
            // const language = SettingsStore.getValueAt(SettingLevel.DEVICE, "language", null, true, true);
            const localSettings = window.localStorage.getItem("mx_local_settings");

            // try to save any 3pid invites from being obliterated and registration time
            // const pendingInvites = ThreepidInviteStore.instance.getWireInvites();
            const registrationTime = window.localStorage.getItem("mx_registration_time");

            // clear local storage
            // Note: validation tokens are stored in IndexedDB (not localStorage) and are
            // therefore unaffected by this clear — no backup/restore needed.
            window.localStorage.clear();

            // if deleteEverything is true, delete user settings too
            if (!deleteEverything) {
                // now restore those invites, registration time and previously set device language
                if (localSettings) {
                    DthSessionStorage.setGenericItem("mx_local_settings", localSettings)
                }

                // if (language) {
                //     await SettingsStore.setValue("language", null, SettingLevel.DEVICE, language);
                // }

                // pendingInvites.forEach(({ roomId, ...invite }) => {
                //     ThreepidInviteStore.instance.storeInvite(roomId, invite);
                // });

                if (registrationTime) {
                    window.localStorage.setItem("mx_registration_time", registrationTime);
                }
            }
        }

        try {
            await StorageAccess.idbClear(TABLE_ACCOUNT_NAME_STORAGE);
        } catch (e) {
            console.error(`[DthSessionStorage] idbClear ${TABLE_ACCOUNT_NAME_STORAGE} failed for account`, e);
        }

        try {
            await StorageAccess.idbClear(TABLE_PICKLEKEY_NAME_STORAGE);
        } catch (e) {
            console.error(`[DthSessionStorage] idbClear ${TABLE_PICKLEKEY_NAME_STORAGE} failed for account`, e);
        }
    }

    //#region TOKENS
    public static async getMatrixAccessToken(): Promise<string | undefined> {
        const pickleKey = await this.getPickleKey();
        return await this.getDecryptedStoredToken(pickleKey, MX_ACCESS_TOKEN_STORAGE_KEY, ACCESS_TOKEN_IV);
    }

    public static async getMatrixRefreshToken(): Promise<string | undefined> {
        const pickleKey = await this.getPickleKey();
        return await this.getDecryptedStoredToken(pickleKey, MX_REFRESH_TOKEN_STORAGE_KEY, REFRESH_TOKEN_IV);
    }

    public static async getApiAccessToken(): Promise<string | undefined> {
        const pickleKey = await this.getPickleKey();
        return await this.getDecryptedStoredToken(pickleKey, API_ACCESS_TOKEN_STORAGE_KEY, ACCESS_TOKEN_IV);
    }

    public static async getApiRefreshToken(): Promise<string | undefined> {
        const pickleKey = await this.getPickleKey();
        return await this.getDecryptedStoredToken(pickleKey, API_REFRESH_TOKEN_STORAGE_KEY, REFRESH_TOKEN_IV);
    }

    /**
     * Timestamp di scadenza dell'access token api in secondi
     * @returns 
     */
    public static getApiExpirationToken(): number {
        const expiration = this.getGenericItem(API_EXPIRATION_TOKEN_KEY);
        return expiration ? Number(expiration) : 0;
    }

    //#endregion
}
